import React, { useState, useMemo, useRef } from "react";
import dayjs, { Dayjs } from "dayjs";
import Header from "../../components/dms_common/Header";
import SummaryCard from "../../components/dms_common/SummaryCard";
import CustomDataGrid from "../../components/dms_common/CustomDataGrid";
import { SummaryCardBox } from "../../components/dms_common/SummaryCard";
import "./SalesReturnScreen.css";
import { salesReturnColumnsMetaData, salesReturnStatusMapping, keyAccountStatusFilterMenuItems, reviewStatusFilterMenuItems, navigateToSalesReturnDetails } from "./SalesReturnUtils";
import { SalesReturnDetail, SaleReturnSummary } from "../../types/types";
import { useEffect } from "react";
import { fetchSalesReturnData, getReturnableInvoiceDetails } from "./SalesReturnService";
import DateFilter from "../deliveryConfirmation/DateFilter";
import StatusFilter from "../deliveryConfirmation/StatusFilter";
import { Loader } from "../../components/loader/Loader";
import RenderSvg from "../../components/renderSvg/RenderSvg";
import { useNavigate } from "react-router-dom";
import { ristrictForwardNavigation } from "../deliveryConfirmation/DeliveryConfirmationService";
import ScreenOverlay from "../../components/dms_common/ScreenOverlay";
import MultiCategoryFilter from "../../components/dms_common/MultiCategoryFilter";
import { downloadAllReports } from "../../services/manageReportService";
import { logEvent } from "firebase/analytics";
import { sendSlackAlert, SlackChannel } from "../../services/slackService";
import { safeStringify } from "../../components/dms_common/DmsUtils";
import { Alert, Button, Snackbar } from "@mui/material";
import { ReactComponent as DownloadAllIcon } from "../../assets/image/svg/downloadAllWhite.svg";
import { analytics } from "../../firebase";
import { getConfigData } from "../../services/configurationService";

const SalesReturnScreen = () => {
  const [summary, setSummary] = useState<SaleReturnSummary>({
    totalOutlets : 0,
    totalReturnCaseQty : 0,
    totalReturnPieceQty : 0,
    totalReturnOtherQty : 0,
    totalReturnAmount : 0,
  });
  const navigate = useNavigate();
  const [allData, setAllData] = useState<SalesReturnDetail[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [reviewStatusFilter, setReviewStatusFilter] = useState<string>("ALL");
  const [keyAccountStatusFilter, setKeyAccountStatusFilter] = useState<string>("ALL");
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [selectedFilterType, setSelectedFilterType] = useState<string>("type");
  const [selectedStatusTypes, setSelectedStatusTypes] = useState<string[]>([]);
  const [selectedKeyAccountStatuses, setSelectedKeyAccountStatuses] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: dayjs().subtract(7, 'day').format("YYYY-MM-DD"),
    endDate: dayjs().format("YYYY-MM-DD"),});
  
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRedirecting, setIsRedirecting] = useState<boolean>(false);
  const [routeIdAndName, setRouteIdAndName] = useState<{[key: string]: any}>({});
  const [selectedRoutes, setSelectedRoutes] = useState<string[]>([]);
  const [selectedLoginIds, setSelectedLoginIds] = useState<string[]>([]);
  const {fetchPricingBySalesrep} = JSON.parse(localStorage.getItem("ConfigDictionary") ?? "{}");
  const [filterSearchValue, setFilterSearchValue] = useState<string>("");
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") || "{}");
  const filterShowRouteLabel:boolean = ConfigDictionary.filterShowRouteLabel ?? false;
  const beatOrRouteLabel = filterShowRouteLabel? "Route":"Beat";
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const salesReturnReportIdRef = useRef<string | null>(null);
  const [summaryCardBoxes, setSummaryCardBoxes] = useState<SummaryCardBox>({
    displaySummaryCards: true,
    sumaryCardValues: [
      {
        boldHeading: "0",
        helperText: "Total No. of Outlets",
        bgColor: "#ECF0FF",
        boldColor: "#1E40CC",
      },
      {
        boldHeading: "0 0 0",
        helperText: "Total Return Quantity",
        bgColor: "#FFF9EF",
        boldColor: "#CC9020",
      },
      {
        boldHeading: "₹0",
        helperText: "Total Return Value",
        bgColor: "#FFF4F2",
        boldColor: "#C44C36",
      },
    ],
  });

  const filters = [
    {
      id: 'type',
      label: 'Type',
      options: keyAccountStatusFilterMenuItems.map(type => ({
        id: type.id,
        label: type.label,
        count: allData.filter(saleReturn => {
          if (type.id === "ALL") return true;
          if (type.id === "KEY ACCOUNTS") return saleReturn.keyAccount === true;
          if (type.id === "NON KEY ACCOUNTS") return saleReturn.keyAccount === false;
          return false;
        }).length
      })),
      selectedOptions: selectedStatusTypes,
      setSelectedOptions: setSelectedStatusTypes,
    },
    {
      id: 'status',
      label: 'Status',
      options: reviewStatusFilterMenuItems.map(status => ({
        id: status.id,
        label: status.label,
        count: allData.filter(saleReturn => {
          if (status.id === "ALL") return true;
          return saleReturn.status?.toLowerCase() === status.id.toLowerCase();
      }).length
      })),
      selectedOptions: selectedKeyAccountStatuses,
      setSelectedOptions: setSelectedKeyAccountStatuses,
    },
    {
      id: 'route',
      label: beatOrRouteLabel,
      options: Object.keys(routeIdAndName).map(route => ({
        id: route,
        label: route,
        count: allData.filter(saleReturn => saleReturn.routeCode && saleReturn.routeCode === routeIdAndName[route]?.routeCode).length
      })),
      selectedOptions: selectedRoutes,
      setSelectedOptions: setSelectedRoutes,
    },
    {
      id: 'salesRepName',
      label: 'SalesRep',
      options: Array.from(new Set(allData.map(saleReturn => saleReturn.salesRepName)))
      .map(salesRepName => ({
        id: salesRepName,
        label: salesRepName,
        count: allData.filter(saleReturn => saleReturn.salesRepName === salesRepName).length
      })),
      selectedOptions: selectedLoginIds,
      setSelectedOptions: setSelectedLoginIds,
    }
  ];
  
  const calculateSummary = (data: SalesReturnDetail[]) => {
    const summaryData = data.reduce(
      (acc, curr) => {
        acc.totalOutlets.add(curr.outletCode);
        acc.totalReturnCaseQty += curr.returnCaseQty;
        acc.totalReturnPieceQty += curr.returnPieceQty;
        acc.totalReturnOtherQty += curr.returnOtherQty;
        acc.totalReturnAmount += curr.returnAmount;
        return acc;
      },
      {
        totalOutlets: new Set(),
        totalReturnCaseQty: 0,
        totalReturnPieceQty: 0,
        totalReturnOtherQty: 0,
        totalReturnAmount: 0,
      }
    );
  
    setSummary({
      totalOutlets: Array.from(summaryData.totalOutlets).length,
      totalReturnCaseQty: summaryData.totalReturnCaseQty,
      totalReturnPieceQty: summaryData.totalReturnPieceQty,
      totalReturnOtherQty: summaryData.totalReturnOtherQty,
      totalReturnAmount: summaryData.totalReturnAmount,
    });
  };

  // Fetch data when dateRange changes
  useEffect(() => {
    const fetchFilteredData = async () => {
      try {
        setIsLoading(true);
        const data = await fetchSalesReturnData(dateRange.startDate, dateRange.endDate);
        setAllData(data);
        calculateSummary(data); 
        const {routeIdAndNameMap} = await getReturnableInvoiceDetails(fetchPricingBySalesrep);
        setRouteIdAndName( routeIdAndNameMap || {});
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching filtered data:", error);
        setIsLoading(false);
      }
    };
    fetchFilteredData();
    if(localStorage.getItem("ristrictForwardNavigation")){
      ristrictForwardNavigation(navigate);
      delete localStorage.ristrictForwardNavigation;
    }
  },[dateRange]);
  const filterData = (
    data: SalesReturnDetail[],
    searchText: string,
    selectedStatusTypes: string[],
    selectedKeyAccountStatuses: string[],
    selectedRoutes: string[],
    selectedLoginIds: string[]
  ) => {
    let filtered = [...data];
    if (selectedStatusTypes.length > 0 && !selectedStatusTypes.includes("ALL")) {
      filtered = filtered.filter(saleReturn => {
        if (selectedStatusTypes.includes("KEY ACCOUNTS")) return saleReturn.keyAccount === true;
        if (selectedStatusTypes.includes("NON KEY ACCOUNTS")) return saleReturn.keyAccount === false;
        return true;
      });
    }
    if (selectedKeyAccountStatuses.length > 0 && !selectedKeyAccountStatuses.includes("ALL")) {
      filtered = filtered.filter(saleReturn =>
        selectedKeyAccountStatuses.includes(saleReturn.status?.toUpperCase() || "")
      );
    }
    if (selectedRoutes.length > 0) {
      filtered = filtered.filter(saleReturn =>
        selectedRoutes.some(selectedRoute =>
          routeIdAndName[selectedRoute]?.routeCode === saleReturn.routeCode
        )
      );
    }
    if (selectedLoginIds.length > 0) {
      filtered = filtered.filter(saleReturn =>
        selectedLoginIds.includes(saleReturn.salesRepName)
      );
    }
    if (searchText) {
      const searchKey = searchText.toLowerCase();
      filtered = filtered.filter(
        (saleReturn) =>
          saleReturn.returnNumber?.toLowerCase().includes(searchKey) ||
          saleReturn.loadoutNumber?.toLowerCase().includes(searchKey) ||
          (saleReturn.invoiceSerialNumber || saleReturn.invoiceNumber)?.toLowerCase().includes(searchKey) ||
          saleReturn.outletCode?.toLowerCase().includes(searchKey) ||
          saleReturn.outletName?.toLowerCase().includes(searchKey) ||
          saleReturn.routeCode?.toLowerCase().includes(searchKey)
      );
    }
    return filtered;
  };

  const filteredData = useMemo(() => {
    return filterData(
      allData,
      searchText,
      selectedStatusTypes,
      selectedKeyAccountStatuses,
      selectedRoutes,
      selectedLoginIds
    );
  }, [allData, searchText, selectedStatusTypes, selectedKeyAccountStatuses, selectedRoutes, selectedLoginIds]);

  const handleDownloadAll = async () => {
    try {
      const startTime = window.performance.now();
      await downloadAllReports(
        salesReturnReportIdRef.current,
        setSnackbarOpen,
        dayjs(dateRange.startDate),
        dayjs(dateRange.endDate),
        "salesReturn",
        "secondary"
      );
      const endTime = window.performance.now();
      logEvent(analytics, "sales_return_report_downloaded", {
        distributor_id: JSON.parse(localStorage.authContext).user.loginId,
        download_format: "xlsx",
        download_time: endTime - startTime,
      });
    } catch (error) {
      await sendSlackAlert(
        SlackChannel.DMS_SECONDARY_UI_NOTIFICATION,
        "Download All Sales Returns Failed - SalesReturnScreen",
        `Failed to download all Sales Return reports for date range ${dateRange.startDate} to ${dateRange.endDate}. Error: ${(error as Error).message}`,
        safeStringify({
          error,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          salesReturnReportId:
            ConfigDictionary.salesReturnReportId ||
            ConfigDictionary.SALES_RETURN_REPORT_ID ||
            "salesReturnReportId",
        })
      );
      logEvent(analytics, "sales_return_report_download_failed", {
        distributor_id: JSON.parse(localStorage.authContext).user.loginId,
        error_reason: error,
      });
    }
    // finally{
    //   setSnackbarOpen(false);
    // }
  };

  useEffect(() => {
    calculateSummary(filteredData);
  }, [filteredData]);

  useEffect(() => {
    setSummaryCardBoxes((prev) => {
      const updated = [...(prev.sumaryCardValues || [])];
      updated[0].boldHeading = String(summary.totalOutlets);
      updated[1].boldHeading = `${summary.totalReturnCaseQty} ${summary.totalReturnOtherQty} ${summary.totalReturnPieceQty}`;
      
      // Convert amount to thousands with 1 decimal place
      const amountInThousands = (summary.totalReturnAmount / 1000).toFixed(1);
      updated[2].boldHeading = `₹${amountInThousands}K`;
      
      return {
        ...prev,
        sumaryCardValues: updated,
      };
    });
  }, [summary]);

  useEffect(() => {
    const fetchSalesReturnReportConfig = async () => {
      const salesReturnReportIdConfig = await getConfigData(
        "dms_configuration",
        "salesReturnReportId"
      );
      salesReturnReportIdRef.current =
        salesReturnReportIdConfig[0]?.configValue ?? "";
    }
    fetchSalesReturnReportConfig();
  }, [])

  const refreshData = ()=>{
    setDateRange({
      startDate: dateRange.startDate,
      endDate: dateRange.endDate
    });
  }

  const handleApply = (startDate: Dayjs, endDate: Dayjs) => {
    setDateRange({
      startDate: startDate.format("YYYY-MM-DD"),
      endDate: endDate.format("YYYY-MM-DD"),
    });
  };

  const handleReset = () => {
    setDateRange({
      startDate: dayjs().subtract(7, 'day').format("YYYY-MM-DD"),
      endDate: dayjs().format("YYYY-MM-DD"),
    });
  };

  const handleCreateNewReturnClick = () => {
    navigate("create");
  };

    const handleSnackbarClose = (
      event?: React.SyntheticEvent | Event,
      reason?: string
    ) => {
      if (reason === "clickaway") {
        return;
      }
      setSnackbarOpen(false);
    };

  return (
    <div className="sales-return-container">
      <ScreenOverlay apply={isRedirecting} />
      <Header
        title="Sale Returns"
        searchFunction={setSearchText}
        onRefresh={refreshData}
        createBtnLabel="Create New Return"
        handleCreateBtnClick={handleCreateNewReturnClick}
      />

      <div className="date-and-key-account-filter-parent">
        <div className="sales-return-container-date">
          <DateFilter onApply={handleApply} onReset={handleReset}></DateFilter>
        </div>

        <Button
          variant="contained"
          sx={{
            textTransform: "none",
            minWidth: "170px",
            backgroundColor: "var(--dashboard-theme-color)",
            ":hover": {
              background: "var(--dashboard-theme-color)",
            },
            borderRadius: "6px",
            boxShadow: "none",
            marginTop:"8px",
          }}
          onClick={handleDownloadAll}
        >
          <DownloadAllIcon style={{ marginRight: "6px" }} />
          Download All
        </Button>
      </div>

      <div className="sale-return-summary-filter">
        <div className="sale-return-summary">
          <SummaryCard
            totalHeight="69"
            summaryTitle="Sale Return Summary"
            summaryTitleBgColor="#FFF9EF"
            box={summaryCardBoxes?.sumaryCardValues}
          />
        </div>
        

          <div className="sale-return-filter">
              <MultiCategoryFilter
                isOpen={filterOpen}
                setIsOpen={setFilterOpen}
                activeFilter={selectedFilterType}
                setActiveFilter={setSelectedFilterType}
                filters={filters}
                label="Type & Status"
                searchValue={filterSearchValue}
                onSearchChange={setFilterSearchValue}
                showSearch={true}
              />
            </div>
          

      </div>
      
      {/* Data Grid */}
      {isLoading ? 
        <Loader />
      : (
        <CustomDataGrid
          rows={Array.isArray(filteredData) ? filteredData : []}
          redirectUrl="view"
          redirectUrlFunction={(url: string, rowData: SalesReturnDetail) => {
            setIsRedirecting(true);
            navigateToSalesReturnDetails(url, rowData, navigate, "Sale Returns")
            .finally(() => setIsRedirecting(false));
          }}
          columnsMetaData={salesReturnColumnsMetaData}
          orderStatusConfig={salesReturnStatusMapping}
          onSalesReturnLandingScreen={true}
        />
      )}
      <div className="sales-return-footer">
        <div className="key-account-indicator">
          <div className="key-account-icon">
            <RenderSvg name='keyAccountIcon' />
          </div>
          <span className="key-account-text">Key Accounts</span>
        </div>
      </div>
      <Snackbar
        open={snackbarOpen}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          variant="filled"
          sx={{ width: "100%" }}
        >
          Your report will be downloaded shortly.
        </Alert>
      </Snackbar>
    </div>
  );
}

export default SalesReturnScreen;